/**
 * File: useAuthorDirectoryQueries.ts
 * Path: apps/admin/src/shared/hooks/useAuthorDirectoryQueries.ts
 * Description: One shared author-list query for the academy and portfolio
 * editors, so the fallback behaviour in `authorDirectory.ts` lives in a single
 * place rather than once per feature.
 */
import { useQuery } from "@tanstack/react-query";
import {
  ADMIN_PAGE_SIZE,
  FirestoreService,
  FIRESTORE_COLLECTIONS,
  limit,
  orderBy,
} from "@idk/config";
import type { UserProfile } from "@idk/types";
import { useAuth } from "@/modules/auth/components/AuthProvider";
import { resolveAuthorDirectory } from "../utils/authorDirectoryUtils";

export const useAuthorDirectory = () => {
  const { user } = useAuth();
  const uid = user?.uid ?? null;

  return useQuery({
    queryKey: [FIRESTORE_COLLECTIONS.users, "authors", uid],
    queryFn: () =>
      resolveAuthorDirectory({
        uid,
        listAll: () =>
          FirestoreService.query<UserProfile>(
            FIRESTORE_COLLECTIONS.users,
            orderBy("updatedAt", "desc"),
            limit(ADMIN_PAGE_SIZE),
          ),
        loadSelf: (ownUid) =>
          FirestoreService.getById<UserProfile>(
            FIRESTORE_COLLECTIONS.users,
            ownUid,
          ),
      }),
  });
};
